import React, { useState, useEffect, useContext, useMemo } from 'react';
import { Chart } from 'primereact/chart';
import { Chart as ChartJS } from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Calendar } from 'primereact/calendar';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';
import SummaryStatsCard, { SUMMARY_STATS_THEMES } from '../components/SummaryStatsCard';
import { getResumenGeneral, getGastosPorRubro, getGastosPorProveedor } from '../service/DashboardService';

const CHART_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#06b6d4', '#a855f7', '#ec4899', '#84cc16'];
const SUMMARY_STATS_THEME_CYCLE = Object.keys(SUMMARY_STATS_THEMES);

// Mismo plugin de texto central que components/Dashboard.js, registrado en su propio
// namespace de Chart.js para no depender de que ese archivo se haya importado antes.
const centerTextPluginGastosConsorcio = {
    id: 'centerTextGastosConsorcio',
    afterDraw(chart) {
        const opts = chart.config?.options?.plugins?.centerTextGastosConsorcio;
        if (!opts?.display) return;
        const { ctx } = chart;
        const { left, right, top, bottom } = chart.chartArea;
        const cx = (left + right) / 2;
        const cy = (top + bottom) / 2;
        ctx.save();
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.font = '500 11px Inter, system-ui, sans-serif';
        ctx.fillStyle = '#667085';
        ctx.fillText('Total gastos:', cx, cy - 14);
        const val = opts.value || '';
        ctx.font = `bold ${val.length > 13 ? 12 : 14}px Inter, system-ui, sans-serif`;
        ctx.fillStyle = '#101828';
        ctx.fillText(val, cx, cy + 8);
        ctx.restore();
    }
};
ChartJS.register(ChartDataLabels, centerTextPluginGastosConsorcio);

const formatARS = (value) =>
    new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(value ?? 0);

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

const GastosConsorcio = () => {
    const { barrio } = useContext(BarrioContext);
    const { administracionId } = useAuth();

    const [selectedDate, setSelectedDate] = useState(new Date());
    const [resumen, setResumen] = useState(null);
    const [gastosPorRubro, setGastosPorRubro] = useState([]);
    const [gastosPorProveedor, setGastosPorProveedor] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!barrio?.id) return;

        const barrioId = barrio.id;
        const d = selectedDate || new Date();
        const periodo = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

        setLoading(true);
        setError(null);
        Promise.all([
            getResumenGeneral(barrioId, administracionId, periodo),
            getGastosPorRubro(barrioId, administracionId, periodo),
            getGastosPorProveedor(barrioId, administracionId, periodo),
        ])
            .then(([resumenRes, rubrosRes, proveedoresRes]) => {
                setResumen(resumenRes.data);
                setGastosPorRubro(rubrosRes.data ?? []);
                setGastosPorProveedor(proveedoresRes.data ?? []);
            })
            .catch((err) => {
                setError(err.response?.data?.errorDisplay || 'Error al cargar los gastos del consorcio');
            })
            .finally(() => setLoading(false));
    }, [selectedDate, barrio?.id, administracionId]);

    const chartRubros = useMemo(() => ({
        labels: gastosPorRubro.map(g => g.grupoGasto),
        datasets: [{
            data: gastosPorRubro.map(g => g.totalRubro),
            backgroundColor: CHART_COLORS.slice(0, gastosPorRubro.length),
            borderWidth: 2,
        }]
    }), [gastosPorRubro]);

    const optionsRubros = useMemo(() => {
        const totalGastos = gastosPorRubro.reduce((sum, g) => sum + g.totalRubro, 0);
        return {
            cutout: '65%',
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: true,
                    position: 'bottom',
                    labels: {
                        boxWidth: 12,
                        padding: 14,
                        font: { size: 11 },
                        generateLabels: (chart) =>
                            chart.data.labels.map((label, i) => ({
                                text: label.length > 28 ? label.slice(0, 28) + '…' : label,
                                fillStyle: CHART_COLORS[i % CHART_COLORS.length],
                                strokeStyle: CHART_COLORS[i % CHART_COLORS.length],
                                hidden: false,
                                index: i,
                            })),
                    }
                },
                tooltip: {
                    callbacks: {
                        label: (ctx) => {
                            const item = gastosPorRubro[ctx.dataIndex];
                            return [
                                ` ${formatARS(item.totalRubro)}`,
                                ` ${item.porcentaje.toFixed(1)}%`,
                                ` ${item.cantidadGastos} gasto(s)`,
                            ];
                        }
                    }
                },
                datalabels: {
                    display: (ctx) => {
                        const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                        return total > 0 && (ctx.dataset.data[ctx.dataIndex] / total) * 100 >= 5;
                    },
                    color: '#fff',
                    anchor: 'center',
                    align: 'center',
                    font: { weight: 'bold', size: 12 },
                    formatter: (value, ctx) => {
                        const total = ctx.dataset.data.reduce((a, b) => a + b, 0);
                        return `${((value / total) * 100).toFixed(1)}%`;
                    },
                    textShadowColor: 'rgba(0,0,0,0.35)',
                    textShadowBlur: 4,
                },
                centerTextGastosConsorcio: {
                    display: true,
                    value: formatARS(totalGastos),
                },
            }
        };
    }, [gastosPorRubro]);

    const proveedoresOrdenados = useMemo(() =>
        [...gastosPorProveedor].sort((a, b) => b.totalProveedor - a.totalProveedor),
    [gastosPorProveedor]);

    const chartProveedores = useMemo(() => ({
        labels: proveedoresOrdenados.map(p => p.nombreProveedor),
        datasets: [{
            label: 'Total',
            data: proveedoresOrdenados.map(p => p.totalProveedor),
            backgroundColor: proveedoresOrdenados.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]),
            borderRadius: 4,
        }]
    }), [proveedoresOrdenados]);

    const optionsProveedores = useMemo(() => ({
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => {
                        const item = proveedoresOrdenados[ctx.dataIndex];
                        return [
                            ` ${formatARS(item.totalProveedor)}`,
                            ` ${item.cantidadGastos} gasto(s)`,
                        ];
                    }
                }
            },
            datalabels: { display: false }
        },
        scales: {
            x: {
                ticks: { callback: (val) => `$${(val / 1000).toFixed(0)}k` },
                grid: { color: '#e5e7eb' }
            },
            y: {
                ticks: { font: { size: 11 } },
                grid: { display: false }
            }
        }
    }), [proveedoresOrdenados]);

    return (
        <>
            <h4>Gastos Consorcio</h4>
            <br />

            <div className="flex align-items-end gap-3 mb-4 flex-wrap">
                <div>
                    <label style={lbl}>Período</label>
                    <Calendar value={selectedDate} onChange={(e) => setSelectedDate(e.value)}
                        view="month" dateFormat="mm/yy" showIcon className="p-inputtext-sm" />
                </div>
            </div>

            <LoadingOverlay visible={loading} />

            {!loading && (
                <div className="grid">
                    {resumen && (
                        <div className="col-12">
                            <div className="grid">
                                <div className="col-12 md:col-6">
                                    <div className="card">
                                        <span className="block text-500 font-medium mb-2">Total general de gastos</span>
                                        <div className="text-900 font-semibold text-3xl">
                                            {formatARS(resumen.totalGeneral)}
                                        </div>
                                    </div>
                                </div>
                                <div className="col-12 md:col-6">
                                    <div className="card">
                                        <span className="block text-500 font-medium mb-2">Cantidad de gastos</span>
                                        <div className="text-900 font-semibold text-3xl">
                                            {resumen.cantidadGastos}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {(gastosPorRubro.length > 0 || gastosPorProveedor.length > 0) && (
                        <>
                            {gastosPorRubro.length > 0 && (
                                <div className="col-12 md:col-4">
                                    <div className="card" style={{ height: '100%' }}>
                                        <h5 className="mt-0">Gastos por grupo</h5>
                                        <Chart
                                            type="doughnut"
                                            data={chartRubros}
                                            options={optionsRubros}
                                            style={{ height: '384px' }}
                                        />
                                    </div>
                                </div>
                            )}
                            {gastosPorProveedor.length > 0 && (
                                <div className="col-12 md:col-8">
                                    <div className="card" style={{ height: '100%', overflow: 'hidden' }}>
                                        <h5 className="mt-0">Gastos por proveedor</h5>
                                        <Chart
                                            type="bar"
                                            data={chartProveedores}
                                            options={optionsProveedores}
                                            style={{ height: `${Math.max(260, proveedoresOrdenados.length * 45)}px` }}
                                        />
                                    </div>
                                </div>
                            )}

                            {gastosPorProveedor.length > 0 && (
                                <div className="col-12">
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1rem' }}>
                                        {proveedoresOrdenados.map((p, idx) => (
                                            <div key={p.proveedorId} style={{ flex: '1 1 180px', minWidth: 180, maxWidth: 260 }}>
                                                <SummaryStatsCard
                                                    icon="pi pi-truck"
                                                    title={p.nombreProveedor}
                                                    theme={SUMMARY_STATS_THEME_CYCLE[idx % SUMMARY_STATS_THEME_CYCLE.length]}
                                                    items={[
                                                        { label: 'Total', value: formatARS(p.totalProveedor) },
                                                        { label: p.cantidadGastos !== 1 ? 'Gastos' : 'Gasto', value: p.cantidadGastos },
                                                    ]}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {error && (
                        <div className="col-12">
                            <div className="card">
                                <span className="text-red-500">{error}</span>
                            </div>
                        </div>
                    )}

                    {!resumen && gastosPorRubro.length === 0 && !error && (
                        <div className="col-12">
                            <div className="card">
                                <p style={{ color: '#98a2b3', textAlign: 'center', margin: '1rem 0' }}>
                                    No hay gastos registrados para el período seleccionado.
                                </p>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </>
    );
};

export default GastosConsorcio;
